import { useRef, useState } from "react";
import {
  CheckCircle2, FileDown, Upload, Loader2, FileCheck, Printer,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * Post-submission success view shown inside the report dialog.
 * - Confirms PDF generation
 * - Big Download / Print button
 * - VERY visible "Upload Signed PDF" section with clear instructions
 *
 * Props:
 *   report       — the freshly created report object (must have id, report_no, pdf_url)
 *   endpoint     — e.g. "/reports/service" | "/reports/installation" | "/reports/vehicle"
 *   onDone       — called when user clicks Done (parent should close dialog & refresh)
 *   onUploaded   — optional, called after a successful signed PDF upload
 */
export default function ReportSuccessPanel({ report, endpoint, onDone, onUploaded }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [signedUrl, setSignedUrl] = useState(report?.signed_pdf_url || "");

  const pdfUrl = report?.pdf_url
    ? `${process.env.REACT_APP_BACKEND_URL}${report.pdf_url}` +
      (report.pdf_url.includes("?") ? "&" : "?") +
      "v=" + encodeURIComponent(report.updated_at || report.created_at || Date.now())
    : "";

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file (the printed & signed copy).");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`${endpoint}/${report.id}/signed`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSignedUrl(data.signed_pdf_url);
      toast.success("Signed PDF uploaded successfully");
      onUploaded?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="report-success-panel" className="space-y-5">
      {/* Step 1 — PDF generated */}
      <div className="border-2 border-emerald-300 bg-emerald-50 p-4 flex items-start gap-3">
        <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-bold text-emerald-900 text-base">
            PDF Generated — {report.report_no}
          </div>
          <div className="text-xs text-emerald-800 mt-0.5">
            Your branded report has been generated. Download and print it for signatures.
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="success-download-pdf"
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-slate-800"
            >
              <FileDown className="w-4 h-4" /> Download PDF
            </a>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-slate-100"
            >
              <Printer className="w-4 h-4" /> Open / Print
            </a>
          </div>
        </div>
      </div>

      {/* Step 2 — Upload signed copy */}
      <div className="border-2 border-amber-400 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <Upload className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-amber-900 text-base">
              Step 2 — Upload Signed PDF
            </div>
            <ol className="text-xs text-amber-900 mt-1 space-y-0.5 list-decimal pl-4">
              <li>Print the generated PDF.</li>
              <li>Collect Field Engineer &amp; Customer signatures on the printed copy.</li>
              <li>Scan it back to PDF, then click <b>Choose Signed PDF</b> below to upload.</li>
            </ol>

            {signedUrl ? (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${signedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="success-signed-link"
                className="mt-3 inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-emerald-700"
              >
                <FileCheck className="w-4 h-4" /> Signed PDF Uploaded — View
              </a>
            ) : (
              <Button
                type="button"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                data-testid="success-upload-signed-btn"
                className="mt-3 rounded-none bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold h-10 uppercase tracking-wider text-xs"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" /> Choose Signed PDF
                  </>
                )}
              </Button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={onFile}
              data-testid="success-signed-file-input"
            />
            <div className="text-[11px] text-amber-800 mt-2 italic">
              You can also upload the signed PDF later from the report list (column &quot;Signed PDF&quot;).
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={onDone}
          data-testid="success-done-btn"
          variant="outline"
          className="rounded-none"
        >
          Done
        </Button>
      </div>
    </div>
  );
}
